import React from 'react';

import { Skeleton } from '../../../../components';
import { useMediaQuery } from '@/nova/hooks/useMediaQuery';
import { MOBILE, MOBILE_MQ } from '@/nova/theme/tokens';
import { useAboveAppChrome } from '../../../../transitions';
import { FILTER_BAR_PX } from '../../game-list-geometry';
import {
  Body,
  GamesHead,
  GamesPane,
  GamesScroll,
  ListRows,
  ListRowsScroll,
  ListsColumn,
  ListsFooter,
  ListsFooterSurface,
  ListsHead,
  ListsTitles,
  ListsTop,
} from '../GameLists/GameLists.styles';
import { Actions as HeadActions, HeadRow, SearchSlot, Side } from '../ListHeading/ListHeading.styles';
import { Grid, Wrap as ListWrap } from '../GameListGrid/GameListGrid.styles';
import { List } from '../GameListRows/GameListRows.styles';
import {
  Field,
  FilterRow,
  ListRowSlot,
  MetaLine,
  Row,
  RowPosition,
  RowText,
  Tile,
  TileArt,
  TileMeta,
  TileName,
  TitleLine,
} from './GameListsSkeleton.styles';

export type GameListsSkeletonProps = {
  /** What is loading: the whole tab on a wide desktop (the page's column of lists beside the open list's), the column of
   *  lists alone (drilled down to a page's lists), or the open list alone (drilled down to its games, or under lists
   *  already drawn). */
  scope: 'tab' | 'lists' | 'list';
  /** The brand's own lists are on and the page's lists can be arranged, so Create a new list has its line in the
   *  column's footer. */
  addList?: boolean;
  /** The open list alone, drilled down to its games: its heading (its name and how many games it holds), then its search
   *  and actions in a row of their own, over them, as the tab draws it there. Under lists already drawn the heading is
   *  the tab's own, so not this. */
  heading?: boolean;
  /** The column's head names the page over its count of lists, as the tab draws it. Where the area header names the
   *  page (on a phone, GameLists' headerOverridden) the column says only its count, so not this. */
  pageName?: boolean;
};

/** Varied, as the lists' names are. */
const ROW_WIDTHS = [132, 158, 104, 146, 118, 138, 96, 124];
const TILES = 12;
const ROWS = 8;

/**
 * The Games tab while its lists load, laid out as the tab will be: the page's column of lists (its name and how many
 * lists it has, a row for each list, Create a new list in its footer), and the open list's heading (its name and
 * how many games it has, its search and its actions at the row's right) over its games, a grid of tiles on a desktop and
 * a list of rows on a phone. On a wide desktop the two side by side in their frames, as tall as the tab draws them;
 * drilled down, the one the owner is at. The containers are the tab's own, frames included, so the placeholders sit
 * where what they stand for will. (Publish changes is in the area header, beside "Game Lists", not in the tab.)
 */
export const GameListsSkeleton = ({
  scope,
  addList = false,
  heading = false,
  pageName = true,
}: GameListsSkeletonProps): React.ReactElement => {
  const isMobile = useMediaQuery(MOBILE);
  // The studio's phone layout (its bottom navigation), up to MOBILE_MQ as the footer's CSS (FIXED_HEADER): there the
  // footer is held over the navigation's scrim as the tab's is, the page's layer over the app's chrome while it is
  // drawn (GameLists' AboveAppChrome), so the real button does not show over a placeholder the scrim tinted.
  const phoneLayout = useMediaQuery(MOBILE_MQ);
  useAboveAppChrome(phoneLayout && addList && scope !== 'list');
  // Add list is a line as tall as a row, Button's own tap target on a phone.
  const actionPx = isMobile ? 48 : 40;
  // On a wide desktop the games scroll inside their column, as a sheet's do.
  const placement = scope === 'tab' ? 'dialog' : 'page';

  const list = (
    <ListWrap $placement={placement} aria-busy={scope === 'list' ? 'true' : undefined}>
      {isMobile ? (
        <List aria-hidden="true">
          {Array.from({ length: ROWS }, (_, i) => (
            <Row key={i} data-testid="skeleton-game">
              <RowPosition>
                <Skeleton width={12} height={12} radius={4} />
              </RowPosition>
              <Skeleton width={40} height={40} radius={8} />
              <RowText>
                <Skeleton width="62%" height={13} />
                <Skeleton width="40%" height={11} />
              </RowText>
            </Row>
          ))}
        </List>
      ) : (
        <Grid aria-hidden="true">
          {Array.from({ length: TILES }, (_, i) => (
            <Tile key={i} data-testid="skeleton-game">
              <TileArt>
                <Skeleton block height="100%" radius={14} />
              </TileArt>
              <TileName>
                <Skeleton width="78%" height={13} />
              </TileName>
              <TileMeta>
                <Skeleton width="56%" height={11} />
              </TileMeta>
            </Tile>
          ))}
        </Grid>
      )}
    </ListWrap>
  );

  // The open list's name and how many games it holds. See ListHeading.
  const listTitles = (
    <ListsTitles>
      <TitleLine>
        <Skeleton width={160} height={18} radius={6} />
      </TitleLine>
      <MetaLine>
        <Skeleton width={64} height={12} />
      </MetaLine>
    </ListsTitles>
  );
  const actions = <Skeleton width={isMobile ? 48 : 140} height={35} radius={999} />;

  // Drilled down: the heading alone, then the list's own row with its search and its actions (on a phone, their icons
  // alone), which pins as the page scrolls.
  if (scope === 'list') {
    return heading ? (
      <>
        <HeadRow data-testid="skeleton-list-heading">{listTitles}</HeadRow>
        <FilterRow>
          <Field data-testid="skeleton-field">
            <Skeleton block height={FILTER_BAR_PX} radius={12} />
          </Field>
          {actions}
        </FilterRow>
        {list}
      </>
    ) : (
      list
    );
  }

  // On a wide desktop the heading carries the search and the actions at its right.
  const listHeading = (
    <HeadRow data-testid="skeleton-list-heading">
      {listTitles}
      <Side>
        <SearchSlot data-testid="skeleton-field">
          <Skeleton block height={FILTER_BAR_PX} radius={12} />
        </SearchSlot>
        <HeadActions>{actions}</HeadActions>
      </Side>
    </HeadRow>
  );

  // The column as the tab draws it: on a wide desktop its head held at the top of the rows' scrolling part, drilled
  // down over it.
  const pane = scope === 'tab';
  const listsHead = (
    <ListsHead data-testid="skeleton-lists-head">
      <ListsTitles>
        {pageName ? (
          <TitleLine>
            <Skeleton width={140} height={18} radius={6} />
          </TitleLine>
        ) : null}
        <MetaLine>
          <Skeleton width={84} height={12} />
        </MetaLine>
      </ListsTitles>
    </ListsHead>
  );
  const lists = (
    <ListsColumn $pane={pane}>
      {pane ? null : listsHead}
      <ListRowsScroll $pane={pane}>
        {pane ? <ListsTop>{listsHead}</ListsTop> : null}
        <ListRows>
          {ROW_WIDTHS.map((width) => (
            <ListRowSlot key={width} data-testid="skeleton-list-row">
              <Skeleton width={width} height={14} />
              <Skeleton width={34} height={22} radius={999} />
            </ListRowSlot>
          ))}
        </ListRows>
        {addList ? (
          <>
            {/* On a phone the footer's surface, under the navigation's scrim, the placeholder over it. */}
            <ListsFooterSurface aria-hidden="true" />
            <ListsFooter $pane={pane}>
              <span data-testid="skeleton-add-list">
                <Skeleton block height={actionPx} radius={999} />
              </span>
            </ListsFooter>
          </>
        ) : null}
      </ListRowsScroll>
    </ListsColumn>
  );

  if (scope === 'lists') return <Body aria-busy="true">{lists}</Body>;

  return (
    <Body $split aria-busy="true">
      {lists}
      <GamesPane>
        <GamesScroll>
          <GamesHead>{listHeading}</GamesHead>
          {list}
        </GamesScroll>
      </GamesPane>
    </Body>
  );
};

export default GameListsSkeleton;
